// Clinical references (G1) — /api/clinical-references (doctors) and
// /api/clinical-library (Admin → Clinical → Reference Library). API contract §6ad.
import { api } from '@/lib/api/client';
import { authFetch } from '../../api';

export type SectionKey = 'key_points' | 'possible_conditions' | 'treatment_approaches' | 'medicine_info' | 'diet_lifestyle';

export interface ReferenceStatement { text: string; citations: number[] }

export interface ReferenceSource {
  n: number;
  chunk_id: number;
  document_id: number;
  title: string;
  version_label: string | null;
  category: string;
  heading: string | null;
  page: number | null;
  excerpt: string;
  similarity: number | null;
}

export interface ReferenceAnswer {
  query_id: number;
  created_at: string;
  question: string;
  insufficient_evidence: boolean;
  reason: string | null;
  sections: Partial<Record<SectionKey, ReferenceStatement[]>>;
  section_labels: Record<SectionKey, string>;
  removed_unsupported: number;
  sources: ReferenceSource[];
  search_mode: 'hybrid' | 'words';
  ai_used: boolean;
  patient_context: { status: 'none' | 'sent' | 'withheld_no_consent'; note: string | null; text: string | null };
  doctor_action: string;
}

export interface ReferenceHistoryItem { id: number; question: string; insufficient_evidence: boolean; doctor_action: string; created_at: string }

export type ReferenceAction = 'inserted' | 'copied' | 'helpful' | 'not_helpful' | 'dismissed';

export const fetchReferenceStatus = () =>
  api.get<{ approved_documents: number; vector_search: boolean; ai_enabled: boolean }>('/clinical-references/status');
export const askReferences = (body: { question: string; visit_id?: number | null; admission_id?: number | null }) =>
  api.post<ReferenceAnswer>('/clinical-references/ask', body);
export const fetchReferenceHistory = () => api.get<ReferenceHistoryItem[]>('/clinical-references/history');
export const fetchReferenceQuery = (id: number) => api.get<ReferenceAnswer>(`/clinical-references/queries/${id}`);
export const recordReferenceAction = (id: number, action: ReferenceAction) =>
  api.post<{ ok: true }>(`/clinical-references/queries/${id}/action`, { action });

/**
 * Puts text at the end of the open Consultation Notes draft (ConsultationChat
 * listens). Nothing happens to an approved note; the doctor still edits and
 * approves the note as usual.
 */
export const INSERT_INTO_NOTE_EVENT = 'hms:insert-into-note';
export function insertIntoNote(text: string) {
  window.dispatchEvent(new CustomEvent(INSERT_INTO_NOTE_EVENT, { detail: { text } }));
}

/** A statement with its sources, as plain text for Copy / Insert into note. */
export function statementWithSources(s: ReferenceStatement, sources: ReferenceSource[]): string {
  const cited = s.citations
    .map((n) => sources.find((x) => x.n === n))
    .filter((x): x is ReferenceSource => !!x)
    .map((x) => `${x.title}${x.version_label ? ` v${x.version_label}` : ''}${x.page ? `, p.${x.page}` : ''}`);
  return `${s.text}${cited.length ? ` (Hospital reference: ${[...new Set(cited)].join('; ')})` : ''}`;
}

// ── Reference Library (admin) ──────────────────────────────────────────────

export type DocStatus = 'draft' | 'pending_approval' | 'approved' | 'rejected' | 'retired';
export type IndexStatus = 'pending' | 'processing' | 'ready' | 'failed';
export const CATEGORIES = ['protocol', 'sop', 'pathway', 'formulary', 'diet', 'policy', 'guideline', 'other'] as const;
export type Category = typeof CATEGORIES[number];
export const CATEGORY_LABEL: Record<Category, string> = {
  protocol: 'Protocol', sop: 'SOP', pathway: 'Clinical pathway', formulary: 'Formulary note',
  diet: 'Diet sheet', policy: 'Policy', guideline: 'Guideline', other: 'Other',
};

export interface LibraryDoc {
  id: number;
  title: string;
  category: Category;
  description: string | null;
  version_label: string | null;
  status: DocStatus;
  file_name: string | null;
  file_type: string | null;
  file_size: number | null;
  pasted: boolean;
  index_status: IndexStatus;
  index_error: string | null;
  indexed_at: string | null;
  chunk_count: number;
  embedding_model: string | null;
  effective_from: string | null;
  review_due: string | null;
  supersedes_id: number | null;
  /** Department (a specialty), optional. */
  specialty_id: number | null;
  department: string | null;
  uploaded_by: string | null;
  uploaded_by_user_id: number | null;
  submitted_at: string | null;
  approved_by: string | null;
  approved_at: string | null;
  rejection_reason: string | null;
  retired_at: string | null;
  retired_by: string | null;
  retire_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface LibraryDocDetail extends LibraryDoc {
  passages: { ordinal: number; heading: string | null; page: number | null; char_count: number; preview: string }[];
  versions: { id: number; title: string; version_label: string | null; status: DocStatus; approved_at: string | null; retired_at: string | null; created_at: string }[];
  times_cited: number;
}

export interface LibraryStatus {
  vector_search: boolean;
  embedding_provider: 'openai' | 'gemini';
  embedding_model: string;
  approved: number;
  pending: number;
  not_embedded: number;
  settings: { embedding_provider: 'openai' | 'gemini'; top_k: number; min_similarity: number; history_retention_days: number };
  can_manage: boolean;
  can_approve: boolean;
}

export const fetchLibraryStatus = () => api.get<LibraryStatus>('/clinical-library/status');
export const fetchLibrary = (q: { status?: string; category?: string; q?: string }) =>
  api.get<LibraryDoc[]>('/clinical-library', { query: { status: q.status || undefined, category: q.category || undefined, q: q.q || undefined } });
export const fetchLibraryDoc = (id: number) => api.get<LibraryDocDetail>(`/clinical-library/${id}`);

export interface NewDocFields {
  title: string; category: Category; version_label?: string; description?: string;
  effective_from?: string; review_due?: string; supersedes_id?: number | null; specialty_id?: number | null;
}

/** Upload a file (multipart) or paste text (JSON). */
export async function addLibraryDoc(fields: NewDocFields, source: { file: File } | { text: string }): Promise<LibraryDoc> {
  if ('text' in source) return api.post<LibraryDoc>('/clinical-library', { ...fields, text: source.text });
  const form = new FormData();
  Object.entries(fields).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') form.append(k, String(v)); });
  form.append('file', source.file);
  const res = await authFetch('/api/clinical-library', { method: 'POST', body: form });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || `Upload failed (${res.status})`);
  return data as LibraryDoc;
}

/** Opens the original file in a new tab (an authenticated blob — this app sends a token, not a cookie). */
export async function openLibraryFile(id: number): Promise<void> {
  const res = await authFetch(`/api/clinical-library/${id}/file`);
  if (!res.ok) throw new Error(`Could not open the file (${res.status})`);
  const url = URL.createObjectURL(await res.blob());
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export const editLibraryDoc = (id: number, body: Partial<NewDocFields>) => api.patch<LibraryDoc>(`/clinical-library/${id}`, body);
export const submitLibraryDoc = (id: number) => api.post<LibraryDoc>(`/clinical-library/${id}/submit`);
export const approveLibraryDoc = (id: number) => api.post<LibraryDoc>(`/clinical-library/${id}/approve`);
export const rejectLibraryDoc = (id: number, reason: string) => api.post<LibraryDoc>(`/clinical-library/${id}/reject`, { reason });
export const retireLibraryDoc = (id: number, reason: string) => api.post<LibraryDoc>(`/clinical-library/${id}/retire`, { reason });
export const reindexLibraryDoc = (id: number) => api.post<LibraryDoc>(`/clinical-library/${id}/reindex`);
export const reindexAll = () => api.post<{ started: number }>('/clinical-library/reindex-all');
export const deleteLibraryDoc = (id: number) => api.delete<{ deleted: true }>(`/clinical-library/${id}`);
export const saveLibrarySettings = (body: Partial<LibraryStatus['settings']>) =>
  api.patch<LibraryStatus['settings']>('/clinical-library/settings', body);

// ── Web Search References (contract §6ai) ──────────────────────────────────
// /api/clinical-web-search (doctors, grant clinical-references:web) and
// /api/clinical-library/web-search (Admin → Reference Library → Web search).

export interface WebSearchResult {
  n: number;
  title: string;
  url: string;
  domain: string;
  source_name: string | null;
  snippet: string;
  /** YYYY, YYYY-MM or YYYY-MM-DD when the source states it. */
  published: string | null;
}

export interface WebSearchSummary {
  /** false when no AI summary was written (AI off, or the AI call failed) — the results still stand. */
  available: boolean;
  insufficient: boolean;
  reason: string | null;
  points: { text: string; citations: number[] }[];
  removed_unsupported: number;
}

export interface WebSearchAnswer {
  query_id: number;
  created_at: string;
  retrieved_at: string;
  question: string;
  provider: string;
  provider_label: string;
  status: 'ok' | 'no_results' | 'error';
  results: WebSearchResult[];
  filtered_out: number;
  summary: WebSearchSummary | null;
  cached: boolean;
  disclaimer: string;
}

export interface WebSearchStatus {
  available: boolean;
  reason: 'disabled' | 'not_configured' | 'department' | 'ai_off' | null;
  message: string | null;
  provider_label: string | null;
  /** The allowed sites, when results are restricted to them. */
  sites: string[] | null;
  summary_enabled: boolean;
  ai_enabled: boolean;
  disclaimer: string;
}

export interface WebSearchHistoryItem { id: number; question: string; status: WebSearchAnswer['status']; result_count: number; created_at: string }

export const fetchWebSearchStatus = () => api.get<WebSearchStatus>('/clinical-web-search/status');
export const runWebSearch = (body: { question: string; visit_id?: number | null; admission_id?: number | null }) =>
  api.post<WebSearchAnswer>('/clinical-web-search/search', body);
export const fetchWebSearchHistory = () => api.get<WebSearchHistoryItem[]>('/clinical-web-search/history');
export const fetchWebSearchQuery = (id: number) => api.get<WebSearchAnswer>(`/clinical-web-search/queries/${id}`);

/** A summary point with its web sources, as plain text for Copy. */
export function webPointWithSources(p: { text: string; citations: number[] }, results: WebSearchResult[]): string {
  const cited = p.citations.map((n) => results.find((r) => r.n === n)).filter((r): r is WebSearchResult => !!r).map((r) => r.url);
  return `${p.text}${cited.length ? ` (Web source, not hospital-approved: ${[...new Set(cited)].join('; ')})` : ''}`;
}

export type WebSearchProvider = 'pubmed' | 'tavily' | 'brave' | 'openai';

export interface WebSearchSettings {
  enabled: boolean;
  provider: WebSearchProvider;
  provider_label: string;
  needs_key: boolean;
  api_key_set: boolean;
  /** A mask when a key is saved — never the key. Sending it back unchanged keeps the saved key. */
  api_key: string;
  allowed_domains: string[];
  blocked_domains: string[];
  restrict_to_allowed: boolean;
  max_results: number;
  timeout_ms: number;
  rate_limit_per_10min: number;
  cache_minutes: number;
  summary_enabled: boolean;
  allowed_specialty_ids: number[];
  unavailable_message: string | null;
  /** OpenAI web search only: overrides the hospital's default OpenAI model; null = the default. */
  openai_model: string | null;
  last_test: { at: string; ok: boolean; message: string } | null;
  updated_at: string;
  updated_by: string | null;
  providers: {
    id: WebSearchProvider; label: string; needs_key: boolean; key_hint: string; fixed_domains: string[] | null;
    /** An AI call (OpenAI): off while AI is off, logged as AI usage, writes its own cited answer. */
    uses_ai: boolean;
    /** The provider always gets at least this long, whatever the timeout setting says. */
    min_timeout_ms: number | null;
  }[];
  warnings: string[];
  can_manage: boolean;
}

export type WebSearchSettingsPatch = Partial<Pick<WebSearchSettings,
  'enabled' | 'provider' | 'api_key' | 'allowed_domains' | 'blocked_domains' | 'restrict_to_allowed' | 'max_results' | 'timeout_ms'
  | 'rate_limit_per_10min' | 'cache_minutes' | 'summary_enabled' | 'allowed_specialty_ids' | 'unavailable_message' | 'openai_model'>> & { clear_api_key?: boolean };

export const fetchWebSearchSettings = () => api.get<WebSearchSettings>('/clinical-library/web-search');
export const saveWebSearchSettings = (body: WebSearchSettingsPatch) => api.patch<WebSearchSettings>('/clinical-library/web-search', body);
/** Saves the form first (when given), then runs one test search. */
export const testWebSearch = (body: WebSearchSettingsPatch) =>
  api.post<{ ok: boolean; message: string; settings: WebSearchSettings }>('/clinical-library/web-search/test', body);
